import { Button, TableCell, TableCellProps, Tooltip, useTheme } from '@mui/material';
import { FC, useState } from 'react';
import { WidgetsData } from '../WidgetRow';
import { Edit } from '@mui/icons-material';
import { ThemeMode } from '../../../../types/config';
import { getNightOrDayPalette } from '../../../../utils/getNightOrDayPalette';

export type WidgetProps = Readonly<{
	expand?: number;
	activityId: string;
	activityName: string;
	widget: WidgetsData;
	activities: (WidgetsData | null | undefined)[];
	handleOnEdit?: () => void;
}>;

type Props = TableCellProps &
	Readonly<{
		expand?: number;
		tooltipText?: string;
		editMode?: boolean;
		showEditOnHover?: boolean;
		handleEditAction?: () => void;
	}>;

export const WidgetCell: FC<Props> = ({
	expand,
	editMode = false,
	tooltipText,
	showEditOnHover,
	handleEditAction,
	children,
	sx,

	...props
}) => {
	const theme = useTheme();
	const [showEdit, setShowEdit] = useState(false);

	return (
		<TableCell
			colSpan={expand}
			onMouseEnter={() => setShowEdit(true)}
			onMouseLeave={() => setShowEdit(false)}
			sx={{
				border: editMode ? `1px solid ${theme.palette.primary[400]}` : `0.5px solid ${theme.palette.grey[200]}`,
				borderTop: editMode ? `1px solid ${theme.palette.primary[400]}` : 'none',
				borderRight: (theme) => (editMode ? `1px solid ${theme.palette.primary[400]}` : `0.5px solid ${theme.palette.grey[200]}`),
				borderLeft: editMode ? `1px solid ${theme.palette.primary[400]}` : 'none',
				backgroundColor: editMode
					? theme.palette.mode === ThemeMode.DARK
						? theme.palette.grey[200]
						: theme.palette.primary[100]
					: 'unset',
				position: 'relative',
				verticalAlign: 'top',
				px: 3,
				py: 2,
				':hover': {
					border: `0.5px solid ${theme.palette.grey[200]}`,
					borderColor: theme.palette[getNightOrDayPalette(theme)][300],
				},
				':last-of-type': {
					borderCollapse: 'collapse',
					border: editMode ? `1px solid ${theme.palette.primary[400]}` : `0.5px solid ${theme.palette.grey[200]}`,
					borderTop: editMode ? `1px solid ${theme.palette.primary[400]}` : '0.5px solid  transparent',
					borderRight: editMode ? `1px solid ${theme.palette.primary[400]}` : '0.5px solid  transparent',
					borderLeft: editMode ? `1px solid ${theme.palette.primary[400]}` : '0.5px solid  transparent',
					':hover': {
						border: `0.5px solid ${theme.palette.grey[200]}`,
						borderColor: theme.palette[getNightOrDayPalette(theme)][300],
					},
				},
				':first-of-type': {
					borderLeft: editMode ? `1px solid ${theme.palette.primary[400]}` : '1px solid transparent',
					borderRight: editMode ? `1px solid ${theme.palette.primary[400]}` : `0.5px solid ${theme.palette.grey[200]}`,
					':hover': {
						border: `0.5px solid ${theme.palette.grey[200]}`,
						borderColor: theme.palette[getNightOrDayPalette(theme)][300],
					},
				},
				fontSize: theme.typography.subtitle1,
				minWidth: 290,
				maxWidth: 290,
				wordBreak: 'break-all',
				color: (theme) => theme.palette[getNightOrDayPalette(theme)][600],
				fontWeight: 500,
				...sx,
			}}
			{...props}
		>
			{showEditOnHover && showEdit && (
				<Tooltip title={tooltipText}>
					<Button
						size={'small'}
						variant={'outlined'}
						color={'secondary'}
						sx={{
							aspectRatio: '1/1',
							minWidth: 0,
							top: '10px',
							right: '10px',
							position: 'absolute',
							padding: 0,
							width: '24px',
							height: '24px',
							backgroundColor: theme.palette.background.default,
							':hover': {
								backgroundColor: theme.palette.background.default,
							},
						}}
						onClick={() => handleEditAction?.()}
					>
						<Edit sx={{ width: 16, height: 16 }} />
					</Button>
				</Tooltip>
			)}
			{children}
		</TableCell>
	);
};
